import { FormReviewData, DestinationHealthInfo } from "@/types";
import { useEffect, useState } from "react";
import {
  medicationService,
  type AIRecommendationsResponse,
  type PharmacistAddedItem,
} from "@/services/medications";
import {
  pharmacyService,
  type VaccineProtocol,
  type MedicationProtocol,
} from "@/services/pharmacy";
import { calculateAge } from "@voyaxhealth/core";
import { usePharmacyContext } from "@/contexts/PharmacyContext";

// Combined type for manual selections with both protocol and pharmacist item data
interface ManualVaccineSelection {
  protocol: VaccineProtocol;
  pharmacistItem: PharmacistAddedItem;
}

interface ManualMedicationSelection {
  protocol: MedicationProtocol;
  pharmacistItem: PharmacistAddedItem;
}

interface PrintableMedicationPrescriptionProps {
  formData: FormReviewData;
  destinationInfoMap: Record<string, DestinationHealthInfo>;
  selectionVersion?: number; // Increment this to trigger re-fetch of recommendations
  aiRecommendations?: AIRecommendationsResponse | null;
}

export const PrintableMedicationPrescription: React.FC<
  PrintableMedicationPrescriptionProps
> = ({
  formData,
  destinationInfoMap,
  selectionVersion,
  aiRecommendations: externalAiRecommendations,
}) => {
  const { currentPharmacy } = usePharmacyContext();
  const [aiRecommendations, setAiRecommendations] =
    useState<AIRecommendationsResponse | null>(
      externalAiRecommendations || null,
    );
  const [routineVaccines, setRoutineVaccines] = useState<VaccineProtocol[]>([]);
  const [travelVaccines, setTravelVaccines] = useState<VaccineProtocol[]>([]);
  const [medications, setMedications] = useState<MedicationProtocol[]>([]);
  const [travelerAge, setTravelerAge] = useState<number>(0);
  const [manualSelections, setManualSelections] = useState<{
    vaccines: ManualVaccineSelection[];
    medications: ManualMedicationSelection[];
  }>({ vaccines: [], medications: [] });
  // Protocol lookup maps for displaying selected items with protocol data
  const [vaccineProtocolMap, setVaccineProtocolMap] = useState<
    Record<string, VaccineProtocol>
  >({});
  const [medicationProtocolMap, setMedicationProtocolMap] = useState<
    Record<string, MedicationProtocol>
  >({});

  // Sync with external recommendations when they change
  useEffect(() => {
    if (externalAiRecommendations) {
      setAiRecommendations(externalAiRecommendations);
    }
  }, [externalAiRecommendations]);

  useEffect(() => {
    const fetchData = async () => {
      if (!formData.travelerId) return;

      try {
        // Calculate traveler's age from date of birth
        // Try multiple sources for date of birth
        const dateOfBirth =
          formData.dateOfBirth || // Top-level field from API
          formData.formData?.personal_info?.date_of_birth ||
          "";

        const ageValue = calculateAge(dateOfBirth);
        const calculatedAge = typeof ageValue === "number" ? ageValue : 0;
        setTravelerAge(calculatedAge);

        // Use external recommendations when available to avoid duplicate API calls
        // The parent component already has fresh data
        let recommendations: AIRecommendationsResponse;
        if (externalAiRecommendations) {
          // Use external recommendations - parent already has latest data
          recommendations = externalAiRecommendations;
        } else {
          // Fallback: fetch only if nothing provided
          recommendations = await medicationService.getAIRecommendations(
            formData.travelerId,
            true,
          );
        }
        setAiRecommendations(recommendations);

        // Fetch all vaccines without age group filter
        const vaccineData = await pharmacyService.getProtocols({
          type: "vaccine",
        });

        // Filter vaccines by category
        const routine = vaccineData.protocols.vaccines.filter(
          (v) => v.category === "routine",
        );
        const travel = vaccineData.protocols.vaccines.filter(
          (v) => v.category === "travel",
        );

        setRoutineVaccines(routine);
        setTravelVaccines(travel);

        // Build vaccine protocol lookup map
        const vaccineMap: Record<string, VaccineProtocol> = {};
        vaccineData.protocols.vaccines.forEach((v) => {
          vaccineMap[v.protocolId] = v;
        });

        // Add protocol data from AI recommendations for selected vaccines
        recommendations.recommendation.vaccines.forEach((v) => {
          if (!vaccineMap[v.protocol_id] && (v as any).protocolMetadata) {
            vaccineMap[v.protocol_id] = {
              protocolId: v.protocol_id,
              displayName: v.vaccine_name,
              therapyClass: v.therapy_class,
              protocolType: "vaccine",
              ageGroup: "adults",
              category: "travel",
              ...(v as any).protocolMetadata,
            } as VaccineProtocol;
          }
        });

        // Add protocol data from pharmacist-added vaccines
        (recommendations.recommendation.pharmacistAddedVaccines || []).forEach(
          (v) => {
            if (!vaccineMap[v.protocol_id] && (v as any).protocolMetadata) {
              vaccineMap[v.protocol_id] = {
                protocolId: v.protocol_id,
                displayName: v.name,
                therapyClass: v.therapy_class,
                protocolType: "vaccine",
                ageGroup: v.age_group || "adults",
                category: "travel",
                ...(v as any).protocolMetadata,
              } as VaccineProtocol;
            }
          },
        );

        // Add protocol data from not recommended vaccines (in case selected)
        (recommendations.recommendation.notRecommended?.vaccines || []).forEach(
          (v) => {
            if (!vaccineMap[v.protocol_id] && (v as any).protocolMetadata) {
              vaccineMap[v.protocol_id] = {
                protocolId: v.protocol_id,
                displayName: v.vaccine_name,
                therapyClass: v.therapy_class,
                protocolType: "vaccine",
                ageGroup: "adults",
                category: "travel",
                ...(v as any).protocolMetadata,
              } as VaccineProtocol;
            }
          },
        );

        setVaccineProtocolMap(vaccineMap);

        // Fetch all medications without age group filter
        const medicationData = await pharmacyService.getProtocols({
          type: "medication",
        });

        setMedications(medicationData.protocols.medications);

        // Build medication protocol lookup map
        const medMap: Record<string, MedicationProtocol> = {};
        medicationData.protocols.medications.forEach((m) => {
          medMap[m.protocolId] = m;
        });

        // Add protocol data from AI recommendations for selected medications
        recommendations.recommendation.medications.forEach((m) => {
          if (!medMap[m.protocol_id] && (m as any).protocolMetadata) {
            medMap[m.protocol_id] = {
              protocolId: m.protocol_id,
              displayName: m.medication_name,
              therapyClass: m.therapy_class,
              protocolType: "medication",
              ageGroup: "adults",
              ...(m as any).protocolMetadata,
            } as MedicationProtocol;
          }
        });

        // Add protocol data from pharmacist-added medications
        (
          recommendations.recommendation.pharmacistAddedMedications || []
        ).forEach((m) => {
          if (!medMap[m.protocol_id] && (m as any).protocolMetadata) {
            medMap[m.protocol_id] = {
              protocolId: m.protocol_id,
              displayName: m.name,
              therapyClass: m.therapy_class,
              protocolType: "medication",
              ageGroup: m.age_group || "adults",
              ...(m as any).protocolMetadata,
            } as MedicationProtocol;
          }
        });

        // Add protocol data from not recommended medications (in case selected)
        (
          recommendations.recommendation.notRecommended?.medications || []
        ).forEach((m) => {
          if (!medMap[m.protocol_id] && (m as any).protocolMetadata) {
            medMap[m.protocol_id] = {
              protocolId: m.protocol_id,
              displayName: m.medication_name,
              therapyClass: m.therapy_class,
              protocolType: "medication",
              ageGroup: "adults",
              ...(m as any).protocolMetadata,
            } as MedicationProtocol;
          }
        });

        setMedicationProtocolMap(medMap);

        // Fetch manual selections from recommendation API (pharmacistAddedVaccines and pharmacistAddedMedications)
        const manualVaccines: ManualVaccineSelection[] = [];
        const manualMedications: ManualMedicationSelection[] = [];

        // Process pharmacist-added vaccines
        const pharmacistAddedVaccines =
          recommendations.recommendation.pharmacistAddedVaccines || [];
        for (const addedVaccine of pharmacistAddedVaccines) {
          if (addedVaccine.isSelected) {
            // Try to find the protocol in the fetched protocols
            let protocol = vaccineData.protocols.vaccines.find(
              (v) => v.protocolId === addedVaccine.protocol_id,
            );

            // If not found, construct from protocolMetadata or create a basic one
            if (!protocol) {
              protocol = {
                protocolId: addedVaccine.protocol_id,
                displayName: addedVaccine.name,
                therapyClass: addedVaccine.therapy_class,
                protocolType: "vaccine",
                ageGroup: addedVaccine.age_group || "adults",
                category: "travel",
                ...(addedVaccine.protocolMetadata || {}),
              } as VaccineProtocol;
            }

            manualVaccines.push({
              protocol,
              pharmacistItem: addedVaccine,
            });
          }
        }

        // Process pharmacist-added medications
        const pharmacistAddedMedications =
          recommendations.recommendation.pharmacistAddedMedications || [];
        for (const addedMedication of pharmacistAddedMedications) {
          if (addedMedication.isSelected) {
            // Try to find the protocol in the fetched protocols
            let protocol = medicationData.protocols.medications.find(
              (m) => m.protocolId === addedMedication.protocol_id,
            );

            // If not found, construct from protocolMetadata or create a basic one
            if (!protocol) {
              protocol = {
                protocolId: addedMedication.protocol_id,
                displayName: addedMedication.name,
                therapyClass: addedMedication.therapy_class,
                protocolType: "medication",
                ageGroup: addedMedication.age_group || "adults",
                ...(addedMedication.protocolMetadata || {}),
              } as MedicationProtocol;
            }

            manualMedications.push({
              protocol,
              pharmacistItem: addedMedication,
            });
          }
        }

        // Process not recommended vaccines that are selected
        const notRecVaccines =
          recommendations.recommendation.notRecommended?.vaccines || [];
        for (const notRecVaccine of notRecVaccines) {
          if ((notRecVaccine as any).isSelected) {
            // Check if already added from pharmacistAddedVaccines
            const alreadyAdded = manualVaccines.some(
              (mv) => mv.protocol.protocolId === notRecVaccine.protocol_id,
            );
            if (!alreadyAdded) {
              // Try to find the protocol in the fetched protocols
              let protocol = vaccineData.protocols.vaccines.find(
                (v) => v.protocolId === notRecVaccine.protocol_id,
              );

              // If not found, construct from protocolMetadata or create a basic one
              if (!protocol) {
                protocol = {
                  protocolId: notRecVaccine.protocol_id,
                  displayName: notRecVaccine.vaccine_name,
                  therapyClass: notRecVaccine.therapy_class,
                  protocolType: "vaccine",
                  ageGroup: "adults",
                  category: "travel",
                  ...((notRecVaccine as any).protocolMetadata || {}),
                } as VaccineProtocol;
              }

              manualVaccines.push({
                protocol,
                pharmacistItem: {
                  protocol_id: notRecVaccine.protocol_id,
                  itemKey: notRecVaccine.protocol_id,
                  name: notRecVaccine.vaccine_name,
                  therapy_class: notRecVaccine.therapy_class,
                  isSelected: true,
                  selectionDetails: (notRecVaccine as any).selectionDetails,
                } as PharmacistAddedItem,
              });
            }
          }
        }

        // Process not recommended medications that are selected
        const notRecMedications =
          recommendations.recommendation.notRecommended?.medications || [];
        for (const notRecMedication of notRecMedications) {
          if ((notRecMedication as any).isSelected) {
            // Check if already added from pharmacistAddedMedications
            const alreadyAdded = manualMedications.some(
              (mm) => mm.protocol.protocolId === notRecMedication.protocol_id,
            );
            if (!alreadyAdded) {
              // Try to find the protocol in the fetched protocols
              let protocol = medicationData.protocols.medications.find(
                (m) => m.protocolId === notRecMedication.protocol_id,
              );

              // If not found, construct from protocolMetadata or create a basic one
              if (!protocol) {
                protocol = {
                  protocolId: notRecMedication.protocol_id,
                  displayName: notRecMedication.medication_name,
                  therapyClass: notRecMedication.therapy_class,
                  protocolType: "medication",
                  ageGroup: "adults",
                  ...((notRecMedication as any).protocolMetadata || {}),
                } as MedicationProtocol;
              }

              manualMedications.push({
                protocol,
                pharmacistItem: {
                  protocol_id: notRecMedication.protocol_id,
                  itemKey: notRecMedication.protocol_id,
                  name: notRecMedication.medication_name,
                  therapy_class: notRecMedication.therapy_class,
                  isSelected: true,
                  selectionDetails: (notRecMedication as any).selectionDetails,
                } as PharmacistAddedItem,
              });
            }
          }
        }

        setManualSelections({
          vaccines: manualVaccines,
          medications: manualMedications,
        });
      } catch (error) {
        // Silent fail
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formData.dateOfBirth,
    formData.formData?.personal_info?.date_of_birth,
    formData.travelerId,
    selectionVersion,
    externalAiRecommendations,
  ]);

  // Helper function to format dates
  const formatDate = (dateString: string) => {
    if (!dateString) return "________________";

    try {
      const datePart = dateString.split("T")[0];
      const parts = datePart.split("-");

      if (parts.length === 3) {
        const year = parseInt(parts[0]);
        const month = parseInt(parts[1]) - 1;
        const day = parseInt(parts[2]);
        const localDate = new Date(year, month, day);

        return localDate.toLocaleDateString("en-US", {
          month: "2-digit",
          day: "2-digit",
          year: "numeric",
        });
      }
      return new Date(dateString).toLocaleDateString("en-US", {
        month: "2-digit",
        day: "2-digit",
        year: "numeric",
      });
    } catch {
      return "________________";
    }
  };

  const currentDate = new Date().toLocaleDateString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
  });

  // Get recommended medications from AI
  const recommendedMedications =
    aiRecommendations?.recommendation.medications.filter(
      (m) => m.status === "RECOMMENDED" || m.status === "CONDITIONAL",
    ) || [];

  // Get patient phone number
  const patientPhone =
    (formData.formData?.personal_info as any)?.phone_number ||
    "________________";

  // Get patient address
  const patientAddress =
    [
      (formData.formData?.personal_info as any)?.address,
      (formData.formData?.personal_info as any)?.city,
      (formData.formData?.personal_info as any)?.state,
      (formData.formData?.personal_info as any)?.zip_code,
    ]
      .filter(Boolean)
      .join(", ") || "________________";

  // Get travel destinations
  const destinations = formData.tripDetails?.destinations || [];
  const destinationsText =
    destinations
      .sort((a, b) => a.order - b.order)
      .map((d) => {
        const city = d.destination_city ? `${d.destination_city}, ` : "";
        return `${city}${d.destination_country}`;
      })
      .join(" → ") || "Not specified";

  // Helper function to check if a vaccine/medication is selected
  const isVaccineSelected = (
    protocolId: string,
    therapyClass?: string,
  ): boolean => {
    if (!aiRecommendations) return false;

    // Check AI recommended vaccines
    const aiSelected = aiRecommendations.recommendation.vaccines.some(
      (v) =>
        v.isSelected &&
        v.protocol_id === protocolId &&
        (!therapyClass ||
          v.therapy_class.toLowerCase() === therapyClass?.toLowerCase()),
    );
    if (aiSelected) return true;

    // Check pharmacist-added vaccines
    const pharmacistSelected = (
      aiRecommendations.recommendation.pharmacistAddedVaccines || []
    ).some(
      (v) =>
        v.isSelected &&
        v.protocol_id === protocolId &&
        (!therapyClass ||
          v.therapy_class?.toLowerCase() === therapyClass?.toLowerCase()),
    );
    if (pharmacistSelected) return true;

    // Check not recommended vaccines (in case selected)
    const notRecSelected = (
      aiRecommendations.recommendation.notRecommended?.vaccines || []
    ).some((v) => (v as any).isSelected && v.protocol_id === protocolId);
    if (notRecSelected) return true;

    return false;
  };

  // Helper function to get yellow fever requirement information from vaccine data
  const getYellowFeverRequirement = (vaccine: any): string | null => {
    // Check if vaccine has REQUIRED_FOR_ENTRY flag
    if (vaccine.flags?.includes("REQUIRED_FOR_ENTRY")) {
      return "REQUIRED";
    }

    // Check destination info for additional context
    for (const destInfo of Object.values(destinationInfoMap)) {
      const yellowFeverVaccines = [
        ...destInfo.health_recommendations.vaccine_details.required,
        ...destInfo.health_recommendations.vaccine_details.conditional,
      ].filter(
        (v) =>
          v.vaccine_code === "yellow_fever" ||
          v.disease_code === "yellow_fever" ||
          v.vaccine.toLowerCase().includes("yellow fever"),
      );

      if (yellowFeverVaccines.length > 0) {
        const destVaccine = yellowFeverVaccines[0];
        // Check if parsed_details contains entry requirement information
        if (destVaccine.parsed_details?.entry_requirements) {
          const entryReqs = destVaccine.parsed_details.entry_requirements;

          // Required for entry
          if (entryReqs.required && !entryReqs.conditional_entry) {
            return `Required for entry to ${destInfo.destination.name}`;
          }

          // Conditional entry (not required from USA)
          if (
            entryReqs.conditional_entry &&
            entryReqs.from_us_direct === "not_required"
          ) {
            return `Not required from USA; required from yellow fever countries for ${destInfo.destination.name}`;
          }
        }

        // Fall back to summary if available
        if (destVaccine.summary) {
          return `${destVaccine.summary} (${destInfo.destination.name})`;
        }
      }
    }
    return null;
  };

  // Helper to check if a therapy class indicates yellow fever
  const isYellowFever = (therapyClass?: string): boolean => {
    if (!therapyClass) return false;
    return therapyClass.toLowerCase().includes("yellow fever");
  };

  // Helper to get yellow fever specific dosing based on age
  const getYellowFeverDosing = (vaccine: any): string => {
    if (travelerAge >= 18) {
      return vaccine.dose || "0.5 mL";
    }
    // Pediatric dosing (typically same for YF but can be customized)
    return vaccine.dose || "0.5 mL";
  };

  // Helper to format dosing info - handles adult and pediatric cases
  const formatDosing = (protocol: any): string => {
    if (!protocol?.dosing) return "N/A";

    const adultDose = protocol.dosing.adult?.dose;
    const pediatricDosing = protocol.dosing.pediatric;
    const hasPediatric =
      Array.isArray(pediatricDosing) && pediatricDosing.length > 0;

    const parts: string[] = [];

    // Add adult dose if available
    if (adultDose) {
      parts.push(`Adult: ${adultDose}`);
    }

    // Add all pediatric doses with age ranges
    if (hasPediatric) {
      pediatricDosing.forEach((ped: any) => {
        if (ped.age_range && ped.dose) {
          parts.push(`${ped.age_range}: ${ped.dose}`);
        }
      });
    }

    if (parts.length === 0) return "N/A";
    return parts.join("; ");
  };

  // Helper to format route info
  const formatRoute = (protocol: any): string => {
    if (!protocol) return "injection";

    const adultRoute = protocol.dosing?.adult?.route;
    const protocolRoute = protocol.route;

    if (adultRoute) return adultRoute;
    if (protocolRoute) return protocolRoute;

    // Default to injection for vaccines
    return "injection";
  };

  // Helper to format timing - handles both string (AI recommendations) and object (protocols) formats
  const formatTiming = (timing: any, protocol?: any): string => {
    // If timing is a string (from AI recommendations), return it directly
    if (typeof timing === "string" && timing.trim()) {
      return timing;
    }

    // If timing is an object (from protocol), format it
    if (timing && typeof timing === "object") {
      const parts: string[] = [];

      if (timing.minimum_days_before_travel) {
        parts.push(
          `At least ${timing.minimum_days_before_travel} days before travel`,
        );
      } else if (timing.start_days_before) {
        parts.push(`Start ${timing.start_days_before} days before travel`);
      }

      if (timing.days_after_leaving) {
        parts.push(`Continue ${timing.days_after_leaving} days after leaving`);
      }

      if (timing.booster_interval) {
        parts.push(`Booster: ${timing.booster_interval}`);
      }

      if (parts.length > 0) {
        return parts.join("; ");
      }
    }

    // Try to get timing from protocol if provided
    if (protocol?.timing) {
      return formatTiming(protocol.timing);
    }

    // Also check protocolMetadata timing
    if (protocol?.protocolMetadata?.timing) {
      return formatTiming(protocol.protocolMetadata.timing);
    }

    return "As directed";
  };

  // Helper to extract the quantity number from medication data
  const extractQuantityNumber = (med: any): string => {
    // First check if there's a pharmacist-set quantity in selectionDetails (nested format)
    if (med?.selectionDetails?.quantity != null && med.selectionDetails.quantity !== "") {
      return String(med.selectionDetails.quantity);
    }

    // Check if there's a quantity at the top level (flat format from API)
    // This handles the case where API returns: { quantity: "1525", refillInfo: "2", ... }
    if (med?.quantity != null && med.quantity !== "") {
      return String(med.quantity);
    }

    // Fall back to parsing quantity_reasoning
    if (!med?.quantity_reasoning) return "-";

    // Look for patterns like "X tablets" or "X doses" (usually the larger number)
    const matches = med.quantity_reasoning.match(/\d+/g);
    if (!matches) return "-";

    // If there are multiple numbers, take the largest one (likely the total quantity)
    const numbers = matches.map(Number);
    return String(Math.max(...numbers));
  };

  // Helper to extract just the number from refill info
  const extractRefillNumber = (refillInfo?: string): string => {
    if (!refillInfo) return "-";

    // Extract the first number from the string, removing words like "shots", "need", etc.
    const match = refillInfo.match(/\d+/);
    return match ? match[0] : "-";
  };

  const isMedicationSelected = (
    protocolId: string,
    _therapyClass?: string,
  ): boolean => {
    if (!aiRecommendations) return false;

    // Check AI recommended medications - only match by protocol_id
    const aiSelected = aiRecommendations.recommendation.medications.some(
      (m) => m.isSelected && m.protocol_id === protocolId,
    );
    if (aiSelected) return true;

    // Check pharmacist-added medications
    const pharmacistSelected = (
      aiRecommendations.recommendation.pharmacistAddedMedications || []
    ).some((m) => m.isSelected && m.protocol_id === protocolId);
    if (pharmacistSelected) return true;

    // Check not recommended medications (in case selected)
    const notRecSelected = (
      aiRecommendations.recommendation.notRecommended?.medications || []
    ).some((m) => (m as any).isSelected && m.protocol_id === protocolId);
    if (notRecSelected) return true;

    return false;
  };

  // Helper function to get pharmacist note for a vaccine
  const getVaccinePharmacistNote = (
    protocolId: string,
    therapyClass?: string,
  ): string => {
    if (!aiRecommendations) return "";

    // Check AI recommended vaccines
    const vaccine = aiRecommendations.recommendation.vaccines.find(
      (v) =>
        v.isSelected &&
        v.protocol_id === protocolId &&
        (!therapyClass ||
          v.therapy_class.toLowerCase() === therapyClass?.toLowerCase()),
    );
    if (vaccine?.selectionDetails?.pharmacistNote) {
      return vaccine.selectionDetails.pharmacistNote;
    }

    // Check pharmacist added vaccines
    const pharmacistVaccine =
      aiRecommendations.recommendation.pharmacistAddedVaccines?.find(
        (v) =>
          v.protocol_id === protocolId &&
          (!therapyClass ||
            v.therapy_class?.toLowerCase() === therapyClass?.toLowerCase()),
      );
    if (pharmacistVaccine?.selectionDetails?.pharmacistNote) {
      return pharmacistVaccine.selectionDetails.pharmacistNote;
    }

    // Check not recommended vaccines (in case selected)
    const notRecVaccine =
      aiRecommendations.recommendation.notRecommended?.vaccines?.find(
        (v) => v.protocol_id === protocolId,
      );
    if ((notRecVaccine as any)?.selectionDetails?.pharmacistNote) {
      return (notRecVaccine as any).selectionDetails.pharmacistNote;
    }

    return "";
  };

  // Helper function to get pharmacist note for a medication
  const getMedicationPharmacistNote = (
    protocolId: string,
    therapyClass?: string,
  ): string => {
    if (!aiRecommendations) return "";

    // Check AI recommended medications
    const medication = aiRecommendations.recommendation.medications.find(
      (m) =>
        m.isSelected &&
        (m.protocol_id === protocolId ||
          m.therapy_class.toLowerCase() === therapyClass?.toLowerCase()),
    );
    if (medication?.selectionDetails?.pharmacistNote) {
      return medication.selectionDetails.pharmacistNote;
    }

    // Check pharmacist added medications
    const pharmacistMedication =
      aiRecommendations.recommendation.pharmacistAddedMedications?.find(
        (m) =>
          m.protocol_id === protocolId &&
          (!therapyClass ||
            m.therapy_class?.toLowerCase() === therapyClass?.toLowerCase()),
      );
    if (pharmacistMedication?.selectionDetails?.pharmacistNote) {
      return pharmacistMedication.selectionDetails.pharmacistNote;
    }

    // Check not recommended medications (in case selected)
    const notRecMedication =
      aiRecommendations.recommendation.notRecommended?.medications?.find(
        (m) => m.protocol_id === protocolId,
      );
    if ((notRecMedication as any)?.selectionDetails?.pharmacistNote) {
      return (notRecMedication as any).selectionDetails.pharmacistNote;
    }

    return "";
  };

  // Helper function to get pharmacist note for manual selections from formData.selections
  const getManualPharmacistNote = (
    itemType: string,
    itemKey: string,
  ): string => {
    if (!formData.selections) return "";

    const selection = formData.selections.find(
      (s) =>
        s.item_type === itemType &&
        s.item_key === itemKey &&
        s.action === "selected" &&
        s.source !== "ai_recommended",
    );

    return selection?.note || "";
  };

  return (
    <div
      style={{
        fontFamily: "Arial, sans-serif",
        padding: "20px 30px",
        maxWidth: "11in",
        margin: "0 auto",
        backgroundColor: "white",
      }}
    >
      {/* Print styles */}
      <style>
        {`
          @media print {
            .prescription-content {
              overflow: visible !important;
            }
            .prescription-content table {
              page-break-inside: auto;
            }
            .prescription-content thead {
              display: table-header-group;
            }
            .prescription-content td.notes-cell {
              max-width: 100px;
              overflow: visible;
              word-wrap: break-word;
              word-break: break-word;
              font-size: 8px;
            }
            .prescription-final-text {
              text-align: center;
              font-style: italic;
              font-size: 10px;
              margin-top: 20px;
            }
            .page-signature {
              position: fixed;
              bottom: 0;
              left: 0;
              right: 0;
              padding: 8px 20px;
              background: white;
              font-size: 10px;
              border-top: 1px solid #000;
            }
            @page {
              margin: 15mm 15mm 20mm 15mm;
              size: auto;
            }
          }
          @media screen {
            .prescription-final-text {
              text-align: center;
              font-style: italic;
              font-size: 10px;
              margin-top: 20px;
              padding-top: 10px;
            }
            .page-signature {
              display: none;
            }
          }
        `}
      </style>

      {/* Main content wrapper */}
      <div className="prescription-content">
        {/* Header */}
        <div
          style={{
            textAlign: "center",
            marginBottom: "20px",
            paddingBottom: "15px",
            borderBottom: "1px solid #000",
          }}
        >
          <h1
            style={{
              margin: "0 0 4px 0",
              fontSize: "24px",
              fontWeight: "bold",
            }}
          >
            {currentPharmacy?.name?.toUpperCase() || "VOYAX HEALTH"}
          </h1>
          {currentPharmacy?.location && (
            <div style={{ fontSize: "11px", marginBottom: "4px" }}>
              {currentPharmacy.location}
            </div>
          )}
          <div style={{ fontSize: "12px", lineHeight: "1.4" }}>
            <div>Travel Health & Medication Checklist</div>
          </div>
        </div>

        {/* Patient Info Header */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginBottom: "15px",
            padding: "10px",
            border: "1px solid #000",
            fontSize: "12px",
          }}
        >
          <div>
            <strong>Patient:</strong> {formData.travelerName}
          </div>
          <div>
            <strong>DOB:</strong>{" "}
            {formatDate(
              formData.dateOfBirth ||
                formData.formData?.personal_info?.date_of_birth ||
                "",
            )}
          </div>
          <div>
            <strong>Date:</strong> {currentDate}
          </div>
          <div>
            <strong>Consultation #:</strong> {formData.consultationNumber}
          </div>
        </div>

        {/* Travel Information */}
        <div
          style={{
            marginBottom: "15px",
            padding: "10px",
            border: "1px solid #000",
            fontSize: "12px",
          }}
        >
          <div style={{ marginBottom: "5px" }}>
            <strong>Travel Destinations:</strong> {destinationsText}
          </div>
          <div style={{ display: "flex", gap: "20px" }}>
            <div>
              <strong>Departure Date:</strong>{" "}
              {formatDate(formData.tripDetails?.departureDate || "")}
            </div>
            <div>
              <strong>Return Date:</strong>{" "}
              {formatDate(formData.tripDetails?.returnDate || "")}
            </div>
          </div>
        </div>

        {/* Pharmacist Selections Section */}
        {(aiRecommendations &&
          (aiRecommendations.recommendation.vaccines.some(
            (v) => v.isSelected,
          ) ||
            aiRecommendations.recommendation.medications.some(
              (m) => m.isSelected,
            ))) ||
        manualSelections.vaccines.length > 0 ||
        manualSelections.medications.length > 0 ? (
          <div style={{ marginBottom: "15px" }}>
            <h3
              style={{
                fontSize: "16px",
                fontWeight: "bold",
                marginBottom: "10px",
                borderBottom: "1px solid #000",
                paddingBottom: "5px",
              }}
            >
              Rx
            </h3>

            {/* Selected Vaccines */}
            {((aiRecommendations &&
              aiRecommendations.recommendation.vaccines.filter(
                (v) => v.isSelected,
              ).length > 0) ||
              manualSelections.vaccines.length > 0) && (
              <div style={{ marginTop: "12px" }}>
                <h4
                  style={{
                    fontSize: "14px",
                    fontWeight: "bold",
                    marginBottom: "8px",
                  }}
                >
                  Rx - Vaccines
                </h4>
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    marginBottom: "15px",
                    fontSize: "11px",
                  }}
                >
                  <thead>
                    <tr style={{}}>
                      <th
                        style={{
                          border: "1px solid #000",
                          padding: "6px 4px",
                          textAlign: "left",
                          fontWeight: "bold",
                        }}
                      >
                        ✓
                      </th>
                      <th
                        style={{
                          border: "1px solid #000",
                          padding: "6px 4px",
                          textAlign: "left",
                          fontWeight: "bold",
                        }}
                      >
                        Vaccine Name
                      </th>
                      <th
                        style={{
                          border: "1px solid #000",
                          padding: "6px 4px",
                          textAlign: "left",
                          fontWeight: "bold",
                          width: "120px",
                        }}
                      >
                        Dosage
                      </th>
                      <th
                        style={{
                          border: "1px solid #000",
                          padding: "6px 4px",
                          textAlign: "left",
                          fontWeight: "bold",
                          width: "60px",
                        }}
                      >
                        Admin Site
                      </th>
                      <th
                        style={{
                          border: "1px solid #000",
                          padding: "6px 4px",
                          textAlign: "left",
                          fontWeight: "bold",
                          width: "80px",
                        }}
                      >
                        Timing
                      </th>
                      <th
                        style={{
                          border: "1px solid #000",
                          padding: "6px 4px",
                          textAlign: "left",
                          fontWeight: "bold",
                          width: "50px",
                        }}
                      >
                        Refills
                      </th>
                      <th
                        style={{
                          border: "1px solid #000",
                          padding: "6px 4px",
                          textAlign: "left",
                          fontWeight: "bold",
                          width: "180px",
                        }}
                      >
                        Notes
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {aiRecommendations &&
                      aiRecommendations.recommendation.vaccines
                        .filter((v) => v.isSelected)
                        .map((vaccine, index) => {
                          // Look up the protocol for enhanced data
                          const protocol =
                            vaccineProtocolMap[vaccine.protocol_id];
                          // Check if this is a yellow fever vaccine
                          const isYF = isYellowFever(vaccine.therapy_class);

                          return (
                            <tr key={index}>
                              <td
                                style={{
                                  border: "1px solid #000",
                                  padding: "6px 4px",
                                  textAlign: "center",
                                  fontWeight: "bold",
                                }}
                              >
                                V
                              </td>
                              <td
                                style={{
                                  border: "1px solid #000",
                                  padding: "6px 4px",
                                }}
                              >
                                <div>
                                  {protocol?.displayName ||
                                    vaccine.vaccine_name}
                                  {isYF && (
                                    <span
                                      style={{
                                        marginLeft: "4px",
                                        fontSize: "9px",
                                        fontWeight: "600",
                                      }}
                                    >
                                      [
                                      {travelerAge >= 18
                                        ? "Adult"
                                        : "Pediatric"}{" "}
                                      dosing]
                                    </span>
                                  )}
                                </div>
                              </td>
                              <td
                                style={{
                                  border: "1px solid #000",
                                  padding: "6px 4px",
                                  fontSize: "9px",
                                  width: "120px",
                                }}
                              >
                                {formatDosing(protocol)}
                              </td>
                              <td
                                style={{
                                  border: "1px solid #000",
                                  padding: "6px 4px",
                                  fontSize: "9px",
                                  width: "60px",
                                }}
                              >
                                {vaccine.selectionDetails?.administrationSite || "-"}
                              </td>
                              <td
                                style={{
                                  border: "1px solid #000",
                                  padding: "6px 4px",
                                  fontSize: "9px",
                                  width: "80px",
                                }}
                              >
                                {formatTiming(vaccine.timing, protocol)}
                              </td>
                              <td
                                style={{
                                  border: "1px solid #000",
                                  padding: "6px 4px",
                                  fontSize: "9px",
                                  width: "50px",
                                  textAlign: "center",
                                }}
                              >
                                {extractRefillNumber(vaccine.selectionDetails?.refillInfo)}
                              </td>
                              <td
                                className="notes-cell"
                                style={{
                                  border: "1px solid #000",
                                  padding: "6px 4px",
                                  fontSize: "9px",
                                  width: "180px",
                                }}
                              >
                                {vaccine.selectionDetails?.pharmacistNote ||
                                  "-"}
                              </td>
                            </tr>
                          );
                        })}
                    {/* Manual Vaccine Selections */}
                    {manualSelections.vaccines.map((selection, index) => {
                      const isYF = isYellowFever(
                        selection.protocol.therapyClass,
                      );
                      return (
                        <tr key={`manual-${index}`}>
                          <td
                            style={{
                              border: "1px solid #000",
                              padding: "6px 4px",
                              textAlign: "center",
                              fontWeight: "bold",
                            }}
                          >
                            V
                          </td>
                          <td
                            style={{
                              border: "1px solid #000",
                              padding: "6px 4px",
                            }}
                          >
                            {selection.protocol.displayName}
                            {isYF && (
                              <span
                                style={{
                                  marginLeft: "4px",
                                  fontSize: "9px",
                                  fontWeight: "600",
                                }}
                              >
                                [{travelerAge >= 18 ? "Adult" : "Pediatric"}{" "}
                                dosing]
                              </span>
                            )}
                          </td>
                          <td
                            style={{
                              border: "1px solid #000",
                              padding: "6px 4px",
                              fontSize: "9px",
                              width: "120px",
                            }}
                          >
                            {formatDosing(selection.protocol) || "As directed"}
                          </td>
                          <td
                            style={{
                              border: "1px solid #000",
                              padding: "6px 4px",
                              fontSize: "9px",
                              width: "60px",
                            }}
                          >
                            {selection.pharmacistItem.selectionDetails?.administrationSite || "-"}
                          </td>
                          <td
                            style={{
                              border: "1px solid #000",
                              padding: "6px 4px",
                              fontSize: "9px",
                              width: "80px",
                            }}
                          >
                            {formatTiming(
                              selection.protocol.timing,
                              selection.protocol,
                            )}
                          </td>
                          <td
                            style={{
                              border: "1px solid #000",
                              padding: "6px 4px",
                              fontSize: "9px",
                              width: "50px",
                              textAlign: "center",
                            }}
                          >
                            {extractRefillNumber(selection.pharmacistItem.selectionDetails?.refillInfo)}
                          </td>
                          <td
                            className="notes-cell"
                            style={{
                              border: "1px solid #000",
                              padding: "6px 4px",
                              fontSize: "9px",
                              width: "180px",
                            }}
                          >
                            {selection.pharmacistItem.selectionDetails
                              ?.pharmacistNote || "-"}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Selected Medications - on new page */}
            {((aiRecommendations &&
              aiRecommendations.recommendation.medications.filter(
                (m) => m.isSelected,
              ).length > 0) ||
              manualSelections.medications.length > 0) && (
              <div style={{ pageBreakBefore: "always", paddingTop: "20px" }}>
                <h4
                  style={{
                    fontSize: "14px",
                    fontWeight: "bold",
                    marginBottom: "8px",
                  }}
                >
                  Rx - Medications
                </h4>
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    marginBottom: "15px",
                    fontSize: "11px",
                  }}
                >
                  <thead>
                    <tr>
                      <th
                        style={{
                          border: "1px solid #000",
                          padding: "6px 4px",
                          textAlign: "left",
                          fontWeight: "bold",
                        }}
                      >
                        ✓
                      </th>
                      <th
                        style={{
                          border: "1px solid #000",
                          padding: "6px 4px",
                          textAlign: "left",
                          fontWeight: "bold",
                        }}
                      >
                        Medication Name
                      </th>
                      <th
                        style={{
                          border: "1px solid #000",
                          padding: "6px 4px",
                          textAlign: "left",
                          fontWeight: "bold",
                          width: "80px",
                        }}
                      >
                        Dosage
                      </th>
                      <th
                        style={{
                          border: "1px solid #000",
                          padding: "6px 4px",
                          textAlign: "left",
                          fontWeight: "bold",
                          width: "40px",
                        }}
                      >
                        QTY
                      </th>
                      <th
                        style={{
                          border: "1px solid #000",
                          padding: "6px 4px",
                          textAlign: "left",
                          fontWeight: "bold",
                          width: "90px",
                        }}
                      >
                        Instructions
                      </th>
                      <th
                        style={{
                          border: "1px solid #000",
                          padding: "6px 4px",
                          textAlign: "left",
                          fontWeight: "bold",
                          width: "50px",
                        }}
                      >
                        Refills
                      </th>
                      <th
                        style={{
                          border: "1px solid #000",
                          padding: "6px 4px",
                          textAlign: "left",
                          fontWeight: "bold",
                          width: "180px",
                        }}
                      >
                        Notes
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {aiRecommendations &&
                      aiRecommendations.recommendation.medications
                        .filter((m) => m.isSelected)
                        .map((med, index) => {
                          // Look up the protocol for enhanced data
                          const protocol =
                            medicationProtocolMap[med.protocol_id];

                          return (
                            <tr key={index}>
                              <td
                                style={{
                                  border: "1px solid #000",
                                  padding: "6px 4px",
                                  textAlign: "center",
                                  fontWeight: "bold",
                                }}
                              >
                                V
                              </td>
                              <td
                                style={{
                                  border: "1px solid #000",
                                  padding: "6px 4px",
                                }}
                              >
                                <div>
                                  {protocol?.displayName || med.medication_name}
                                </div>
                              </td>
                              <td
                                style={{
                                  border: "1px solid #000",
                                  padding: "6px 4px",
                                  fontSize: "9px",
                                  width: "120px",
                                }}
                              >
                                {formatDosing(protocol)}
                              </td>
                              <td
                                style={{
                                  border: "1px solid #000",
                                  padding: "6px 4px",
                                  fontSize: "9px",
                                  width: "40px",
                                  textAlign: "center",
                                }}
                              >
                                {extractQuantityNumber(med)}
                              </td>
                              <td
                                style={{
                                  border: "1px solid #000",
                                  padding: "6px 4px",
                                  fontSize: "9px",
                                  width: "130px",
                                }}
                              >
                                {protocol?.dosing?.adult?.instructions || "-"}
                              </td>
                              <td
                                style={{
                                  border: "1px solid #000",
                                  padding: "6px 4px",
                                  fontSize: "9px",
                                  width: "50px",
                                  textAlign: "center",
                                }}
                              >
                                {extractRefillNumber(med.selectionDetails?.refillInfo)}
                              </td>
                              <td
                                className="notes-cell"
                                style={{
                                  border: "1px solid #000",
                                  padding: "6px 4px",
                                  fontSize: "9px",
                                  width: "180px",
                                }}
                              >
                                {med.selectionDetails?.pharmacistNote || "-"}
                              </td>
                            </tr>
                          );
                        })}
                    {/* Manual Medication Selections */}
                    {manualSelections.medications.map((selection, index) => (
                      <tr key={`manual-${index}`}>
                        <td
                          style={{
                            border: "1px solid #000",
                            padding: "6px 4px",
                            textAlign: "center",
                            fontWeight: "bold",
                          }}
                        >
                          V
                        </td>
                        <td
                          style={{
                            border: "1px solid #000",
                            padding: "6px 4px",
                          }}
                        >
                          {selection.protocol.displayName}
                        </td>
                        <td
                          style={{
                            border: "1px solid #000",
                            padding: "6px 4px",
                            fontSize: "9px",
                            width: "80px",
                          }}
                        >
                          {formatDosing(selection.protocol) || "As directed"} •{" "}
                          {formatRoute(selection.protocol)}
                        </td>
                        <td
                          style={{
                            border: "1px solid #000",
                            padding: "6px 4px",
                            fontSize: "9px",
                            width: "40px",
                            textAlign: "center",
                          }}
                        >
                          {extractQuantityNumber(selection.pharmacistItem)}
                        </td>
                        <td
                          style={{
                            border: "1px solid #000",
                            padding: "6px 4px",
                            fontSize: "9px",
                            width: "130px",
                          }}
                        >
                          {selection.protocol.dosing?.adult?.instructions ||
                            "-"}
                        </td>
                        <td
                          style={{
                            border: "1px solid #000",
                            padding: "6px 4px",
                            fontSize: "9px",
                            width: "50px",
                            textAlign: "center",
                          }}
                        >
                          {extractRefillNumber(selection.pharmacistItem.selectionDetails?.refillInfo)}
                        </td>
                        <td
                          className="notes-cell"
                          style={{
                            border: "1px solid #000",
                            padding: "6px 4px",
                            fontSize: "9px",
                            width: "180px",
                          }}
                        >
                          {selection.pharmacistItem.selectionDetails
                            ?.pharmacistNote || "-"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ) : null}

        {/* Pharmacist Notes Section */}
        {aiRecommendations?.recommendation.pharmacistNote && (
          <div
            style={{
              marginBottom: "15px",
              padding: "12px 15px",
              border: "2px solid #854d0e",
              borderRadius: "4px",
              backgroundColor: "#fefce8",
            }}
          >
            <h3
              style={{
                margin: "0 0 8px 0",
                fontSize: "13px",
                fontWeight: "bold",
                color: "#854d0e",
              }}
            >
              PHARMACIST NOTES
            </h3>
            <p
              style={{
                margin: "0",
                fontSize: "11px",
                lineHeight: "1.5",
                whiteSpace: "pre-wrap",
              }}
            >
              {aiRecommendations.recommendation.pharmacistNote}
            </p>
            {aiRecommendations.recommendation.pharmacistNoteDetails && (
              <p
                style={{
                  margin: "8px 0 0 0",
                  fontSize: "9px",
                  color: "#92400e",
                  fontStyle: "italic",
                }}
              >
                Added by{" "}
                {
                  aiRecommendations.recommendation.pharmacistNoteDetails.addedBy
                    .name
                }{" "}
                on{" "}
                {new Date(
                  aiRecommendations.recommendation.pharmacistNoteDetails
                    .addedAt,
                ).toLocaleDateString("en-US", {
                  month: "2-digit",
                  day: "2-digit",
                  year: "numeric",
                })}
              </p>
            )}
          </div>
        )}

        {/* OTC/Supplies Recommended Section */}
        <div style={{ marginTop: "20px", pageBreakInside: "avoid" }}>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: "11px",
            }}
          >
            <thead>
              <tr>
                <th
                  style={{
                    border: "1px solid #000",
                    padding: "8px 4px",
                    textAlign: "center",
                    width: "10%",
                    fontWeight: "bold",
                  }}
                >
                  ✓
                </th>
                <th
                  style={{
                    border: "1px solid #000",
                    padding: "8px 4px",
                    textAlign: "left",
                    width: "90%",
                    fontWeight: "bold",
                  }}
                >
                  OTC/Supplies Recommended
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td
                  style={{
                    border: "1px solid #000",
                    padding: "8px 4px",
                    textAlign: "center",
                  }}
                >
                  ☐
                </td>
                <td style={{ border: "1px solid #000", padding: "8px 4px" }}>
                  Nonprescription medications/supplies
                </td>
              </tr>
              <tr>
                <td
                  style={{
                    border: "1px solid #000",
                    padding: "8px 4px",
                    textAlign: "center",
                  }}
                >
                  ☐
                </td>
                <td style={{ border: "1px solid #000", padding: "8px 4px" }}>
                  Insect repellent (DEET, picaridin)
                </td>
              </tr>
              <tr>
                <td
                  style={{
                    border: "1px solid #000",
                    padding: "8px 4px",
                    textAlign: "center",
                  }}
                >
                  ☐
                </td>
                <td style={{ border: "1px solid #000", padding: "8px 4px" }}>
                  Permethrin 0.5%
                </td>
              </tr>
              <tr>
                <td
                  style={{
                    border: "1px solid #000",
                    padding: "8px 4px",
                    textAlign: "center",
                  }}
                >
                  ☐
                </td>
                <td style={{ border: "1px solid #000", padding: "8px 4px" }}>
                  Mosquito net
                </td>
              </tr>
              <tr>
                <td
                  style={{
                    border: "1px solid #000",
                    padding: "8px 4px",
                    textAlign: "center",
                  }}
                >
                  ☐
                </td>
                <td style={{ border: "1px solid #000", padding: "8px 4px" }}>
                  Water purifier
                </td>
              </tr>
              <tr>
                <td
                  style={{
                    border: "1px solid #000",
                    padding: "8px 4px",
                    textAlign: "center",
                  }}
                >
                  ☐
                </td>
                <td style={{ border: "1px solid #000", padding: "8px 4px" }}>
                  Loperamide or BSS
                </td>
              </tr>
              <tr>
                <td
                  style={{
                    border: "1px solid #000",
                    padding: "8px 4px",
                    textAlign: "center",
                  }}
                >
                  ☐
                </td>
                <td style={{ border: "1px solid #000", padding: "8px 4px" }}>
                  Oral rehydration solution
                </td>
              </tr>
              <tr>
                <td
                  style={{
                    border: "1px solid #000",
                    padding: "8px 4px",
                    textAlign: "center",
                  }}
                >
                  ☐
                </td>
                <td style={{ border: "1px solid #000", padding: "8px 4px" }}>
                  Iodine or chlorine dioxide
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      {/* End of prescription-content wrapper */}

      {/* Final footer text - appears at very end of document */}
      <div className="prescription-final-text">
        <strong>{currentPharmacy?.name || "Voyax Health"}</strong>
        {currentPharmacy?.location && ` | ${currentPharmacy.location}`}
      </div>

      {/* Signature footer - appears at bottom of every printed page */}
      <div className="page-signature">
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div>
            <strong>Pharmacist Signature:</strong> ________________________
          </div>
          <div>
            <strong>NPI#:</strong> _______________
          </div>
          <div>
            <strong>Date:</strong> {currentDate}
          </div>
          <div style={{ fontSize: "9px" }}>
            {formData.consultationNumber} | {formData.travelerName}
          </div>
        </div>
      </div>
    </div>
  );
};
